import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface ScrollPositionContextType {
  saveScrollPosition: (key: string, position: number) => void;
  getScrollPosition: (key: string) => number;
  clearScrollPosition: (key: string) => void;
  clearAllScrollPositions: () => void;
  restoreScrollPosition: (key: string) => void;
}

const ScrollPositionContext = createContext<ScrollPositionContextType | undefined>(undefined);

export const ScrollPositionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({});
  const [throttleTimeout, setThrottleTimeout] = useState<NodeJS.Timeout | null>(null);

  // Save scroll position with throttling to avoid too many state updates
  const saveScrollPosition = useCallback((key: string, position: number) => {
    if (throttleTimeout) {
      clearTimeout(throttleTimeout);
    }
    
    // Only update if position has changed significantly (more than 50px)
    // This reduces unnecessary state updates
    const currentPosition = scrollPositions[key] || 0;
    if (Math.abs(currentPosition - position) > 50) {
      const timeout = setTimeout(() => {
        setScrollPositions(prev => ({ ...prev, [key]: position }));
      }, 200); // Increase throttle time to reduce updates
      
      setThrottleTimeout(timeout as unknown as NodeJS.Timeout);
    }
  }, [throttleTimeout, scrollPositions]);

  // Clean up the timeout on unmount
  useEffect(() => {
    return () => {
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [throttleTimeout]);

  const getScrollPosition = useCallback((key: string) => {
    return scrollPositions[key] || 0;
  }, [scrollPositions]);

  const clearScrollPosition = useCallback((key: string) => {
    console.log(`Clearing scroll position for ${key}`);
    setScrollPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[key];
      return newPositions;
    });
  }, []);

  const clearAllScrollPositions = useCallback(() => {
    console.log('Clearing all scroll positions');
    setScrollPositions({});
  }, []);

  // Helper to restore scroll position programmatically
  const restoreScrollPosition = useCallback((key: string) => {
    const position = scrollPositions[key] || 0;
    if (position > 0) {
      console.log(`Restoring scroll position for ${key}: ${position}px`);
      window.scrollTo(0, position);
    }
  }, [scrollPositions]);

  return (
    <ScrollPositionContext.Provider value={{ 
      saveScrollPosition, 
      getScrollPosition, 
      clearScrollPosition,
      clearAllScrollPositions,
      restoreScrollPosition
    }}>
      {children}
    </ScrollPositionContext.Provider>
  );
};

export const useScrollPosition = () => {
  const context = useContext(ScrollPositionContext);
  if (context === undefined) {
    throw new Error('useScrollPosition must be used within a ScrollPositionProvider');
  }
  return context;
};
